/**
 * Who can open a saved report.
 *
 * Sharing a report with the workspace is the one switch this dialog changes.
 * Named editors and viewers are shown but not edited here: the access list of
 * a pipeline report is managed in Report Studio, which owns its design.
 */
import { useState } from 'react'
import { Share2 } from 'lucide-react'
import DialogShell from '@/components/crm/DialogShell'
import type { ReportDefinition } from '@/services/reportDefinitions'
import { Btn } from './ui'

export function ShareDialog({
  definition,
  saving,
  onClose,
  onSave,
}: {
  definition: ReportDefinition
  saving: boolean
  onClose: () => void
  onSave: (isShared: boolean) => void
}) {
  const [shared, setShared] = useState(definition.isShared)
  const changed = shared !== definition.isShared

  return (
    <DialogShell
      label={`Share ${definition.name}`}
      onClose={onClose}
      width="md"
      testId="share-dialog"
      footer={
        <>
          <Btn variant="quiet" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            disabled={!changed}
            busy={saving}
            icon={<Share2 className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => onSave(shared)}
          >
            Save sharing
          </Btn>
        </>
      }
    >
      <div className="flex flex-col gap-3 p-4 text-[13px]">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={shared}
            onChange={(event) => setShared(event.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#FFD86B]"
          />
          <span>
            <span className="block font-medium text-[#F5F3EE]">Shared with the workspace</span>
            <span className="block text-[12px] text-[#8F8F8F]">
              {shared
                ? 'Anyone who can see reports can open it.'
                : 'Private to its owner and the people named below.'}
            </span>
          </span>
        </label>
        <dl className="flex flex-col gap-1.5 border-t border-[#1B1B1B] pt-3 text-[12.5px]">
          <div className="flex justify-between gap-3">
            <dt className="text-[#8F8F8F]">Owner</dt>
            <dd className="break-all text-right text-[#ECEAE4]">{definition.ownerEmail}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-[#8F8F8F]">Editors</dt>
            <dd className="break-all text-right text-[#ECEAE4]">
              {definition.editors.length ? definition.editors.join(', ') : 'Owner only'}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-[#8F8F8F]">Viewers</dt>
            <dd className="break-all text-right text-[#ECEAE4]">
              {definition.viewers.length ? definition.viewers.join(', ') : shared ? 'Everyone in the workspace' : 'None named'}
            </dd>
          </div>
        </dl>
        <p className="text-[11.5px] text-[#8C8C8C]">Each change is saved as a new version of the report.</p>
      </div>
    </DialogShell>
  )
}
